import { uploadClient } from "../../../services/axiosClient";
import { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hook";
import { useTranslation } from "../../localization/hooks";
import { UserVoiceFlows } from "./types";
import { approveUserVoice as approveUserVoiceAction, cloneUserVoice as cloneUserVoiceAction, deleteUserVoice as deleteUserVoiceAction, getUserVoiceById as getUserVoiceByIdAction, getUserVoices as getUserVoicesAction, markVoiceAsProcessing as markVoiceAsProcessingAction, resumeUserVoice as resumeUserVoiceAction, setIsSuccess, updateUserVoice as updateUserVoiceAction, } from "./userVoicesSlice";
export default function useUserVoices() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const userVoicesState = useAppSelector((state) => state.userVoices);
    const userVoices = userVoicesState?.userVoices ?? [];
    const voice = userVoicesState?.voice ?? null;
    const isSuccess = userVoicesState.isSuccess;
    const userVoicesMap = userVoicesState?.userVoicesMap ?? {};
    const loading = userVoicesState?.loading ?? {
        isLoading: false,
        loadingType: null,
    };
    const loadingRef = useRef(loading.isLoading);
    loadingRef.current = loading.isLoading;
    const [userVoiceError, setUserVoiceError] = useState(null);
    const resetError = () => {
        setUserVoiceError(null);
    };
    const getUserVoices = useCallback(async () => {
        if (loadingRef.current)
            return { data: null, error: null };
        try {
            setUserVoiceError(null);
            const result = await dispatch(getUserVoicesAction()).unwrap();
            return { data: result, error: null };
        }
        catch (err) {
            const apiError = err;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.GET_VOICES,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const getUserVoiceById = useCallback(async (id) => {
        try {
            setUserVoiceError(null);
            const result = await dispatch(getUserVoiceByIdAction(id)).unwrap();
            return { data: result, error: null };
        }
        catch (err) {
            const apiError = err;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.GET_VOICE_BY_ID,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const updateUserVoice = useCallback(async (payload) => {
        try {
            setUserVoiceError(null);
            const { sampleFiles, ...rest } = payload;
            const filenames = sampleFiles?.map((entry) => entry instanceof File ? { filename: entry.name } : entry);
            const backendPayload = { ...rest, sample_files: filenames };
            const result = await dispatch(updateUserVoiceAction(backendPayload)).unwrap();
            if (result.presigned_urls?.length) {
                const newFiles = (payload.sampleFiles ?? []).filter((file) => file instanceof File);
                await Promise.all(result.presigned_urls.map(({ uploadUrl }, index) => {
                    const file = newFiles[index];
                    return uploadClient.put(uploadUrl, file, {
                        headers: { "Content-Type": file.type },
                    });
                }));
                await dispatch(markVoiceAsProcessingAction({ voice_id: payload.voice_id })).unwrap();
            }
            return { data: result, error: null };
        }
        catch (err) {
            const apiError = err;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.UPDATE_VOICE,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const deleteUserVoice = useCallback(async (voiceId) => {
        try {
            setUserVoiceError(null);
            await dispatch(deleteUserVoiceAction(voiceId)).unwrap();
            return { data: undefined, error: null };
        }
        catch (err) {
            const apiError = err;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.DELETE_VOICE,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    const cloneUserVoice = useCallback(async (payload) => {
        try {
            const { sampleFiles: cloneSampleFiles, ...cloneRest } = payload;
            const filenames = cloneSampleFiles.map((file) => ({
                filename: file.name,
            }));
            const backendPayload = { ...cloneRest, sample_files: filenames };
            const result = await dispatch(cloneUserVoiceAction(backendPayload)).unwrap();
            if (!result.presigned_urls)
                return {
                    data: null,
                    error: "Couldn't initiate voice cloning. Try again later",
                };
            await Promise.all(result.presigned_urls.map(({ uploadUrl }, index) => {
                const file = cloneSampleFiles[index];
                return uploadClient.put(uploadUrl, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                });
            }));
            await dispatch(markVoiceAsProcessingAction({ voice_id: result.voice_id })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.CLONE_VOICE,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return {
                data: null,
                error: errorMessage,
                errorType: UserVoiceFlows.CLONE_VOICE,
                errorPayload: apiError,
            };
        }
    }, [dispatch, t]);
    const resumeUserVoice = useCallback(async (payload) => {
        try {
            const filenames = payload.sampleFiles.map((file) => ({
                filename: file.name,
            }));
            const backendPayload = {
                ...payload,
                sampleFiles: filenames,
            };
            const { presigned_urls } = await dispatch(resumeUserVoiceAction(backendPayload)).unwrap();
            if (!presigned_urls)
                return {
                    data: null,
                    error: "Couldn't initiate voice resume. Try again later",
                };
            const result = await Promise.all(presigned_urls.map(({ uploadUrl }, index) => {
                const file = payload.sampleFiles[index];
                return uploadClient.put(uploadUrl, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                });
            }));
            await dispatch(markVoiceAsProcessingAction({ voice_id: payload["voice_id"] })).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.RESUME_VOICE,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return {
                data: null,
                error: errorMessage,
                errorType: UserVoiceFlows.RESUME_VOICE,
                errorPayload: apiError,
            };
        }
    }, [dispatch, t]);
    const approveUserVoice = useCallback(async (payload) => {
        try {
            setUserVoiceError(null);
            const result = await dispatch(approveUserVoiceAction(payload)).unwrap();
            return { data: result, error: null };
        }
        catch (err) {
            const apiError = err;
            const errorMessage = apiError?.message ?? t("error:fallbackError");
            setUserVoiceError({
                errorType: UserVoiceFlows.APPROVE_VOICE,
                errorMessage,
                errorCode: apiError?.errorCode,
            });
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, t]);
    return {
        userVoices,
        userVoicesMap,
        voice,
        isSuccess,
        setIsSuccess,
        isLoading: loading.isLoading,
        loadingType: loading.loadingType,
        errorType: userVoiceError?.errorType ?? null,
        errorMessage: userVoiceError?.errorMessage ?? null,
        errorCode: userVoiceError?.errorCode ?? null,
        cloneUserVoice,
        resumeUserVoice,
        getUserVoices,
        getUserVoiceById,
        updateUserVoice,
        deleteUserVoice,
        approveUserVoice,
        resetError,
    };
}
